import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import GenericGridBackground from '@public//images/heros/header-grid-only-desktop.png'
import Image from 'next/image'
import { FC, ReactNode } from 'react'

import { animation } from '@/components/Hero/Hero'
import { RenderOn } from '@/utills/DeviceUtils'

const AnimatedElement = ({ children, index }: { children: ReactNode; index: number }) => {
    return <Box sx={{ ...animation(index) }}>{children}</Box>
}

const Animation = () => {
    const sharedRowStyle = {
        display: 'flex',
        width: '100%',
        maxWidth: '480px',
    }
    return (
        <Box
            sx={{
                width: '45%',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                flexDirection: 'column',
                pl: 15,
            }}
        >
            <Box sx={sharedRowStyle}>
                <AnimatedElement index={1}>
                    <Image
                        alt={`chip-img-tile-1`}
                        src={'/images/heros/customers-tile-1.png'}
                        width={375}
                        height={100}
                    />
                </AnimatedElement>
                <AnimatedElement index={0}>
                    <Image
                        alt={`chip-img-visa`}
                        src={'/images/heros/customers-logo-visa.png'}
                        width={92}
                        height={31}
                        style={{ position: 'absolute', top: -10, left: -20 }}
                    />
                </AnimatedElement>
            </Box>
            <Box sx={sharedRowStyle}>
                <AnimatedElement index={3}>
                    <Image
                        alt={`chip-img-tile-2`}
                        src={'/images/heros/customers-tile-2.png'}
                        width={375}
                        height={100}
                    />
                </AnimatedElement>
                <AnimatedElement index={2}>
                    <Image
                        alt={`chip-img-payhawk`}
                        src={'/images/heros/customers-logo-payhawk.png'}
                        width={122}
                        height={48}
                        style={{ position: 'absolute', left: -420, top: -40 }}
                    />
                </AnimatedElement>
                <AnimatedElement index={4}>
                    <Image
                        alt={`chip-img-gocardless`}
                        src={'/images/heros/customers-logo-gocardless.png'}
                        width={144}
                        height={41}
                        style={{ position: 'absolute', left: -50, top: 35 }}
                    />
                </AnimatedElement>
            </Box>
            <Box sx={sharedRowStyle}>
                <AnimatedElement index={6}>
                    <Image
                        alt={`chip-img-tile-3`}
                        src={'/images/heros/customers-tile-3.png'}
                        width={375}
                        height={204}
                    />
                </AnimatedElement>
                <AnimatedElement index={5}>
                    <Image
                        alt={`chip-img-salesforce`}
                        src={'/images/heros/customers-logo-salesforce.png'}
                        width={96}
                        height={67}
                        style={{ position: 'absolute', left: -480, top: 10 }}
                    />
                </AnimatedElement>
            </Box>
            <Box sx={sharedRowStyle}>
                <AnimatedElement index={8}>
                    <Image
                        alt={`chip-img-tile-4`}
                        src={'/images/heros/customers-tile-4.png'}
                        width={375}
                        height={100}
                    />
                </AnimatedElement>
                <AnimatedElement index={9}>
                    <Image
                        alt={`chip-img-logixboard`}
                        src={'/images/heros/customers-logo-logixboard.png'}
                        width={178}
                        height={48}
                        style={{ position: 'absolute', left: -450, top: 25 }}
                    />
                </AnimatedElement>
                <AnimatedElement index={7}>
                    <Image
                        alt={`chip-img-jas`}
                        src={'/images/heros/customers-logo-jas.png'}
                        width={94}
                        height={36}
                        style={{ position: 'absolute', left: 5, top: -5 }}
                    />
                </AnimatedElement>
            </Box>
        </Box>
    )
}

const CustomersHero: FC = () => {
    const title = 'Meet the companies growing by plugging their green gap'
    const description =
        ' The companies you know and love share how they are winning new customers by accelerating their sustainability goals with Lune.'
    const content = (
        <Box
            sx={{
                pb: 3,
                pt: 9,
                width: {
                    xs: '100%',
                    md: '65%',
                    lg: '50%',
                },
                height: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: {
                    xs: 'space-between',
                    md: 'center',
                },
                flexDirection: 'column',
                gap: {
                    xs: 4,
                    sm: 4,
                    md: 5,
                    lg: 5,
                    xl: 5,
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    gap: {
                        xs: 4,
                        sm: 4,
                        md: 5,
                        lg: 5,
                        xl: 5,
                    },
                }}
            >
                <RenderOn breakPoints={['xs', 'sm']}>
                    <Text variant={'h4'}>{title}</Text>
                </RenderOn>
                <RenderOn breakPoints={['xs', 'sm']}>
                    <Text variant={'body2'}>{description}</Text>
                </RenderOn>
                <RenderOn breakPoints={['md', 'lg', 'xl']}>
                    <Text variant={'h3'}>{title}</Text>
                </RenderOn>
                <RenderOn breakPoints={['md', 'lg', 'xl']}>
                    <Text variant={'body1'}>{description}</Text>
                </RenderOn>
            </Box>
        </Box>
    )

    return (
        <Box
            sx={{
                pb: {
                    xs: '440px',
                    md: '0',
                },
                height: {
                    xs: 'auto',
                    md: '792px',
                },
                mt: '-72px !important',
                position: 'relative',
                '.background-image': {
                    height: '100%',
                    width: 'auto',
                    position: 'absolute',
                    left: '-100%',
                    right: '-100%',
                    margin: 'auto',
                    top: 0,
                    zIndex: '-1',
                },
            }}
        >
            <RenderOn breakPoints={['xs', 'sm']}>
                <Image
                    className={'background-image'}
                    width={1800}
                    height={1440}
                    src={'/images/heros/customers-mobile.png'}
                    alt={'page-hero-mobile'}
                />
            </RenderOn>
            <RenderOn breakPoints={['md', 'lg', 'xl']}>
                <Image
                    className={'background-image'}
                    width={4000}
                    height={1584}
                    src={GenericGridBackground}
                    alt={'page-hero-desktop'}
                />
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {content}
                    <Animation />
                </Box>
            </RenderOn>
            <RenderOn breakPoints={['xs', 'sm']}>{content}</RenderOn>
        </Box>
    )
}

export default CustomersHero
